import React, { useState, useEffect } from 'react';
import { CounselorScheduleResponse, CounselorDaySchedule, CounselorTimeSlot } from 'models/Admin/AdminPaginationTypes';
import styled from 'styled-components';
import { ModalBackground, ModalLayout } from 'components/Modal/ModalForms';
import { CloseButtonWrapper } from 'styles/Modal/modalStyle';
import CloseDialogue from 'assets/Icons/DeleteIcon/CloseDialogue';
import BasicButton from 'components/Buttons/BasicButton';
import { font, palette } from 'styles/Global/globalStyles';
import { Body3Medium } from 'styles/Global/typography';
import Loading from 'components/Common/Loading';

interface LoadingState {
    day: number;
    timeIndex: number;
    isLoading: boolean;
}

interface CounselerScheduleModalProps {
    toggle: () => void;
    scheduleData: CounselorScheduleResponse;
    onScheduleUpdate: (day: number, timeSlot: string, isAvailable: boolean) => Promise<void>;
}

const CounselerScheduleModal = ({ toggle, scheduleData, onScheduleUpdate }: CounselerScheduleModalProps) => {
    const [localSchedule, setLocalSchedule] = useState<CounselorScheduleResponse>(scheduleData);
    const [loadingState, setLoadingState] = useState<LoadingState | null>(null);

    useEffect(() => {
        setLocalSchedule(scheduleData);
    }, [scheduleData]);

    const getDayName = (day: number) => {
        const days = ['월요일', '화요일', '수요일', '목요일', '금요일', '토요일', '일요일'];
        return days[day];
    };

    const formatTime = (time: string) => {
        return time;
    };

    const handleTimeSlotChange = async (day: number, timeIndex: number, timeSlot: string) => {
        try {
            // 이미 로딩 중이면 중복 호출 방지
            if (loadingState !== null) return;

            const newSchedule = { ...localSchedule };
            const daySchedule = newSchedule.slots.find((slot) => slot.day === day);

            if (!daySchedule || !daySchedule.slot[timeIndex]) return;

            const newIsAvailable = !daySchedule.slot[timeIndex].isAvailable;

            // 로딩 상태 설정
            setLoadingState({ day, timeIndex, isLoading: true });

            // API 업데이트 호출
            await onScheduleUpdate(day, timeSlot, newIsAvailable);

            // 성공 시 로컬 상태 업데이트
            daySchedule.slot[timeIndex].isAvailable = newIsAvailable;
            setLocalSchedule(newSchedule);
        } catch (error) {
            console.error('스케줄 업데이트 실패:', error);
            // 에러 발생 시 원래 상태로 복원
            setLocalSchedule(scheduleData);
        } finally {
            // 로딩 상태 초기화
            setLoadingState(null);
        }
    };

    return (
        <ModalBackground>
            <ModalLayout $width='1000px' $height='90vh'>
                <ModalTitle>상담사 스케줄 관리</ModalTitle>
                <ContentWrapper>
                    <ScheduleContainer>
                        {localSchedule.slots.map((daySchedule: CounselorDaySchedule) => (
                            <DaySection key={daySchedule.day}>
                                <DayTitle>{getDayName(daySchedule.day)}</DayTitle>
                                <TimeGrid>
                                    {daySchedule.slot.map((timeSlot: CounselorTimeSlot, index: number) => (
                                        <TimeSlot key={index}>
                                            <TimeLabel>{formatTime(timeSlot.time)}</TimeLabel>
                                            <CheckboxWrapper>
                                                <Checkbox
                                                    type='checkbox'
                                                    checked={timeSlot.isAvailable}
                                                    onChange={() => handleTimeSlotChange(daySchedule.day, index, timeSlot.time)}
                                                    disabled={loadingState !== null}
                                                />
                                            </CheckboxWrapper>
                                        </TimeSlot>
                                    ))}
                                </TimeGrid>
                            </DaySection>
                        ))}
                    </ScheduleContainer>
                </ContentWrapper>
                <ButtonContainer>
                    <BasicButton $width='160px' $height='48px' $type='anotherFill' onClick={toggle}>
                        닫기
                    </BasicButton>
                </ButtonContainer>
                <CloseButtonWrapper onClick={toggle}>
                    <CloseDialogue />
                </CloseButtonWrapper>
                {loadingState && (
                    <LoadingOverlay>
                        <Loading loading={true} />
                    </LoadingOverlay>
                )}
            </ModalLayout>
        </ModalBackground>
    );
};

const ModalTitle = styled(Body3Medium)`
    margin-bottom: 24px;
`;

const ContentWrapper = styled.div`
    flex: 1;
    overflow-y: auto;
    padding-right: 8px;
    margin-right: -8px;
    height: calc(90vh - 140px);

    &::-webkit-scrollbar {
        width: 8px;
    }

    &::-webkit-scrollbar-track {
        background: ${palette.lightGray};
        border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
        background: ${palette.gray};
        border-radius: 4px;
    }
`;

const ScheduleContainer = styled.div`
    width: 100%;
    border: 1px solid ${palette.lightGray};
    border-radius: 4px;
    padding: 20px;
`;

const DaySection = styled.div`
    &:not(:last-child) {
        margin-bottom: 24px;
        padding-bottom: 24px;
        border-bottom: 1px solid ${palette.lightGray};
    }
`;

const DayTitle = styled.h3`
    font-size: 16px;
    font-weight: ${font.weight.medium};
    color: ${palette.black};
    margin-bottom: 16px;
`;

const TimeGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 12px;
`;

const TimeSlot = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    background-color: ${palette.white};
    border-radius: 4px;
    border: 1px solid ${palette.lightGray};
`;

const TimeLabel = styled.span`
    font-size: 15px;
    color: ${palette.gray};
    flex: 1;
    margin-right: 16px;
`;

const CheckboxWrapper = styled.div`
    width: 18px;
    height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const Checkbox = styled.input`
    cursor: pointer;
    width: 18px;
    height: 18px;
    accent-color: ${palette.primary};
    flex-shrink: 0;
`;

const ButtonContainer = styled.div`
    display: flex;
    justify-content: center;
    gap: 8px;
    margin-top: 24px;
`;

const LoadingOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;
    backdrop-filter: blur(0.5px);
`;

export default CounselerScheduleModal;
